import { markHydrationComplete } from '@on-arte/ui';
import React from 'react';
import ReactDOM from 'react-dom';
import { QueryClient } from 'react-query';
import { DehydratedState, hydrate } from 'react-query/hydration';
import { BrowserRouter } from 'react-router-dom';
import 'reflect-metadata';

import { RoutingState } from '@onArte/frontend/interfaces';
import { plRouting, SsrRoutingSnapshot, ssrRoutingStateWindowKey, ssrStateWindowKey } from '@onArte/shared/constants';
import { getRouteDetailsByUrl } from '@onArte/shared/utils';

import App from './frontend/app/app';
import './frontend/translations/translations';

const queryClient: QueryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: false } },
});

/**
 * Dane pobrane przez serwer trafiają do pamięci podręcznej ZANIM React zacznie
 * podłączanie. Bez tego pierwsze renderowanie w przeglądarce zobaczyłoby pusty
 * cache, wyrenderowało pustą listę i React zgłosiłby rozjazd z HTML-em przysłanym
 * przez serwer — a użytkownik zobaczyłby mignięcie pustej strony.
 */
const ssrState: DehydratedState | null =
  (window as unknown as Record<string, DehydratedState | null>)[ssrStateWindowKey] ?? null;

if (ssrState) {
  hydrate(queryClient, ssrState);
}

/**
 * Stan routingu wyliczony przez serwer. Przychodzi jako wycinek (SsrRoutingSnapshot),
 * więc brakujące części — stałą tablicę tras i obiekt bieżącej trasy — odtwarzamy tutaj.
 */
const ssrRoutingSnapshot: SsrRoutingSnapshot | null =
  (window as unknown as Record<string, SsrRoutingSnapshot | null>)[ssrRoutingStateWindowKey] ?? null;

const ssrRoutingState: RoutingState | undefined = ssrRoutingSnapshot
  ? {
    routes: plRouting,
    elementParamsFromRouting: ssrRoutingSnapshot.elementParamsFromRouting,
    currentWebsiteUrl: ssrRoutingSnapshot.currentWebsiteUrl as RoutingState['currentWebsiteUrl'],
    currentRouteObject: getRouteDetailsByUrl(window.location.pathname),
  }
  : undefined;

ReactDOM.hydrate(
  <React.StrictMode>
    <BrowserRouter>
      <App queryClient={queryClient} initialRoutingState={ssrRoutingState} />
    </BrowserRouter>
  </React.StrictMode>,
  document.getElementById('root'),
  // Od tej chwili komponenty wybierające wariant układu po szerokości ekranu mogą
  // odpowiadać od razu. W trakcie podłączania muszą trzymać wariant serwerowy, inaczej
  // React dostaje inne drzewo niż w HTML-u; po podłączeniu czekanie daje już tylko
  // widoczny przeskok przy przejściach wewnątrz aplikacji.
  markHydrationComplete
);
